import { useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import DeleteIcon from "@mui/icons-material/Delete";
import { Link, Stack, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { formatNumber } from "@/features/shared";
import { useSnackbar } from "@/features/ui";

export const DownPaymentAllocationsSection = () => {
  const { control } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "purchaseDownPaymentAllocations",
  });

  const allocations = useWatch({
    control,
    name: "purchaseDownPaymentAllocations",
  });

  const handleDeleteClick = useCallback(
    (id) => () => {
      const index = allocations.findIndex((row) => row.id === id);
      if (index !== -1) {
        remove(index);
        showSnackbar("Uang muka berhasil dihapus", "success");
      }
    },
    [allocations, remove, showSnackbar],
  );

  const columns = useMemo(
    () => [
      {
        field: "referenceNumber",
        headerName: "No. Faktur",
        flex: 2,
        renderCell: ({ row }) => (
          <Link
            component={RouterLink}
            to={`/procurement/purchase-down-payments/${row.referenceNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {row.referenceNumber}
          </Link>
        ),
      },
      {
        field: "availableAmount",
        headerName: "Tersedia",
        flex: 1,
        type: "number",
        valueFormatter: (value) => formatNumber(value || 0),
      },
      {
        field: "amount",
        headerName: "Dialokasikan",
        flex: 1,
        type: "number",
        editable: true,
        valueSetter: (value, row) => {
          const newValue = value == null ? 0 : value;
          return { ...row, amount: newValue };
        },
        valueFormatter: (_, row) => formatNumber(row.amount || 0),
      },
      {
        field: "note",
        headerName: "Catatan",
        flex: 2,
        valueGetter: (value) => value || "-",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Aksi",
        getActions: ({ id }) => [
          <GridActionsCellItem
            key={id}
            icon={<DeleteIcon />}
            label="Hapus"
            onClick={handleDeleteClick(id)}
            color="error"
          />,
        ],
      },
    ],
    [handleDeleteClick],
  );

  const processRowUpdate = (newRow) => {
    return new Promise((resolve, reject) => {
      const amount = Number(newRow.amount);

      if (Number.isNaN(amount) || amount < 0) {
        reject(new Error("Nominal tidak boleh kurang dari 0"));
        return;
      }
      if (amount > newRow.availableAmount) {
        reject(
          new Error(
            `Nominal tidak boleh melebihi sisa tersedia (${formatNumber(
              newRow.availableAmount,
            )})`,
          ),
        );
        return;
      }

      const index = allocations.findIndex((row) => row.id === newRow.id);
      if (index === -1) {
        reject(new Error("Alokasi tidak ditemukan"));
        return;
      }

      update(index, { ...newRow, amount });
      resolve({ ...newRow, amount });
    });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Uang Muka</Typography>
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        rows={allocations}
        columns={columns}
        getRowId={(row) => row.id}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          showSnackbar(error.message, "error");
        }}
        disableRowSelectionOnClick
        hideFooter
      />
    </Stack>
  );
};
